import { parsePhoneNumberFromString } from 'libphonenumber-js';
import moment from 'moment-timezone';
import * as yup from 'yup';

import { flexibleDate, flexibleDateString } from '$Utils';

// Convenience method for validating phone numbers
yup.addMethod(yup.mixed, 'phone', function (country = 'US') {
    return this.test({
        test: (value = '') => {
            const parseNumber = parsePhoneNumberFromString(value, country);
            return value === '' || (parseNumber && parseNumber.isValid());
        },
        message: `Must be a valid ${country} phone number`
    });
});

yup.addMethod(yup.string, 'passwordStrength', function (strengthRequired) {
    return this.required('Password cannot be blank')
        .min(8, 'Password must be at least 8 characters long')
        .test({
            test: (value, context) => {
                return context.parent.password_score >= strengthRequired;
            },
            message: 'Password must be stronger'
        });
});

yup.addMethod(yup.string, 'confirmPassword', function (fieldNameToMatch) {
    return this.required('Password cannot be blank').test({
        test: (value, context) => {
            return value === context.parent[fieldNameToMatch];
        },
        message: 'Passwords must match'
    });
});

yup.addMethod(
    yup.date,
    'limitDateRange',
    function ({
        minNum,
        minUnit = 'days',
        minDate = 'now',
        maxNum,
        maxUnit = 'days',
        maxDate = 'now',
        updateCarePlan = false
    }) {
        function formatUnit(unit, num) {
            return num === 1 && unit.slice(-1).toLowerCase() === 's'
                ? unit.slice(0, -1)
                : num !== 1 && unit.slice(-1).toLowerCase() !== 's'
                ? `${unit}s`
                : unit;
        }

        function checkIfToday(date) {
            return flexibleDateString(date) === flexibleDateString('now');
        }
        const minIsToday = checkIfToday(minDate);
        const maxIsToday = checkIfToday(maxDate);

        function validator(direction) {
            const isMin = direction === 'min';
            const num = isMin ? minNum : maxNum;
            const unit = isMin ? minUnit : maxUnit;

            if (isNaN(num))
                return `Cannot be in the ${isMin ? 'past' : 'future'}`;

            const formattedUnit = formatUnit(unit, num);

            const isToday = isMin ? minIsToday : maxIsToday;
            const validationDate = isMin ? minDate : maxDate;

            if (updateCarePlan) {
                return isMin
                    ? 'Must be after previous cycle start date'
                    : `Must be no later than a year from today`;
            }

            return isToday && num === 0
                ? `Should be no ${isMin ? 'earlier' : 'later'} than today`
                : `Should be no ${isMin ? 'earlier' : 'later'} than ${
                      num !== 0 ? num : ''
                  } ${num !== 0 ? `${formattedUnit} from` : ''} ${
                      isToday
                          ? 'today'
                          : flexibleDate(validationDate).format('MM/DD/YYYY')
                  }`;
        }

        return this.min(
            flexibleDate(minDate)
                .subtract(minNum, minUnit)
                .format('MM/DD/YYYY'),
            validator('min')
        ).max(
            flexibleDate(maxDate).add(maxNum, maxUnit).format('MM/DD/YYYY'),
            validator('max')
        );
    }
);

yup.addMethod(yup.string, 'validateInitial', function ({ label }, match) {
    return this.test({
        test: (value = '') => {
            if (!value || !match) return true;
            if (value.charAt(0).toUpperCase() !== match) {
                return false;
            }
            return true;
        },
        message: `${label} "${match}"`
    });
});

yup.addMethod(
    yup.date,
    'validBirthday',
    function (minimumAge = 13, minLabel, maxLabel) {
        return this.min(
            moment().subtract(123, 'years'),
            maxLabel || 'Must be less than 123 years old'
        ).max(
            moment().subtract(minimumAge, 'years'),
            minLabel || 'Date cannot be in the future'
        );
    }
);

export default yup;
